/**
 * Saved mash and fermentation schedules (PLAN-PROFILES P1).
 *
 * The fourth and fifth profile libraries, following the shape `equipment.ts`
 * and `water-profile.ts` already established: a record on the workspace, keyed
 * by id, each entry carrying `id` / `name` / `builtin` / `notes` and the thing
 * itself. Four verbs repeated across five libraries is what makes a sixth free.
 *
 * ## They hold recipe steps, unchanged
 *
 * `steps` is `RecipeMashStep[]` and `RecipeFermentationStep[]` — the same
 * arrays a recipe stores, ids and all. That is not laziness: it means the mash
 * table and the fermentation table render a *profile* with no second component
 * and no mapping layer, and it means applying one is a copy rather than a
 * translation. A step's `stepId` is regenerated on apply, because two recipes
 * built from one profile must not share step ids.
 *
 * ## Applying copies, and that is the whole difference from equipment
 *
 * A recipe references its equipment profile by id (`RecipeDoc.equipmentProfileId`)
 * and re-reads it on every calculation, which is right: it describes a rig that
 * really did change when the brewer changed it.
 *
 * These do the opposite, and must. A mash schedule is part of what the recipe
 * *is*. If applying "Hochkurz" left a reference behind, then editing that
 * profile would silently redesign every lager in the library — including one
 * already in the fermenter, whose brew sheet would stop matching the beer. So
 * apply copies the steps in and the link ends there, which is the same
 * snapshot rule PLAN §6 applies to ingredients and `recipe.water` applies to a
 * source analysis.
 *
 * The profiles page groups these with equipment and water because they are all
 * *things you reuse*. It has to say, per tab, which kind of binding it is; the
 * grouping is about the brewer's intent, not about the mechanism.
 */

import type {
  FermentationStep as CoreFermentationStep,
  MashStep as CoreMashStep,
  NamedSchedule,
} from '@beerwright/core';
import { FERMENTATION_SCHEDULES, MASH_SCHEDULES } from '@beerwright/core';
import { z } from 'zod';
import { documentIdSchema, nameSchema, notesSchema, timestampSchema } from './common';
import { recipeFermentationStepSchema, recipeMashStepSchema } from './ingredients';

/** The fields every saved schedule carries, whichever kind it is. */
const scheduleProfileFields = {
  /** Stable profile id, unique within its library. */
  id: documentIdSchema,
  /** Display name, e.g. `Full body` or `My house pils`. */
  name: nameSchema,
  /** True for the schedules this package ships; those are read-only. */
  builtin: z.boolean(),
  /** Brewer's prose: what it is for, and what it does to the beer. */
  notes: notesSchema,
  /** Creation time, **ms since epoch**. */
  createdAt: timestampSchema,
  /** Last-modification time, **ms since epoch**. */
  updatedAt: timestampSchema,
} as const;

/** A saved mash schedule. */
export const mashProfileDocSchema = z.strictObject({
  ...scheduleProfileFields,
  /** The rests, in execution order. Copied into a recipe verbatim. */
  steps: z.array(recipeMashStepSchema).max(16),
});

/** @see mashProfileDocSchema */
export type MashProfileDoc = z.infer<typeof mashProfileDocSchema>;

/** A saved fermentation schedule. */
export const fermentationProfileDocSchema = z.strictObject({
  ...scheduleProfileFields,
  /** The stages, in execution order. Copied into a recipe verbatim. */
  steps: z.array(recipeFermentationStepSchema).max(16),
});

/** @see fermentationProfileDocSchema */
export type FermentationProfileDoc = z.infer<typeof fermentationProfileDocSchema>;

/**
 * Fields an update may change.
 *
 * `steps` is replaced whole rather than patched per step. A schedule is edited
 * as a table and saved as a table, and a per-step command set would be four
 * more verbs for a list that is at most sixteen rows and is never edited by two
 * people at once.
 *
 * Nothing here carries a `.default()`, deliberately: these are `.partial()`-ed
 * into the update payload, and `.partial()` does not strip a default — the key
 * would materialise in every patch that omits it and overwrite what the brewer
 * did not touch. `recipeFermentationStepChangesSchema` and `DECOCTION_FIELDS`
 * are the two places that rule is already written down.
 */
export const mashProfileChangesSchema = mashProfileDocSchema
  .omit({ id: true, builtin: true, createdAt: true, updatedAt: true })
  .partial();

/** @see mashProfileChangesSchema */
export const fermentationProfileChangesSchema = fermentationProfileDocSchema
  .omit({ id: true, builtin: true, createdAt: true, updatedAt: true })
  .partial();

// ---------------------------------------------------------------------------
// The shipped set
// ---------------------------------------------------------------------------

/**
 * Turn one of core's named schedules into a read-only profile document.
 *
 * The engine owns the schedules and this package owns documents, so this is the
 * one place the two meet. Ids are core's own, prefixed by kind so a mash and a
 * fermentation profile can never collide in one record; step ids are derived
 * from the profile id and the position, which keeps a built-in byte-identical
 * across reloads (`withShippedBuiltins` overwrites it every boot, and a random
 * id would make that a spurious change every time).
 *
 * Timestamps are 0 for the reason `BUILTIN_EQUIPMENT_PROFILES` uses 0: these
 * documents predate the workspace, and stamping them with a wall clock would
 * break replay.
 */
function toMashProfile(schedule: NamedSchedule<CoreMashStep>): MashProfileDoc {
  return {
    id: schedule.id,
    name: schedule.name,
    builtin: true,
    notes: schedule.description,
    createdAt: 0,
    updatedAt: 0,
    steps: schedule.steps.map((step, index) => ({
      stepId: `${schedule.id}-${index}`,
      name: step.name,
      type: step.type,
      targetTempC: step.targetTempC,
      timeMinutes: step.timeMinutes,
      infusionVolumeL: step.infusionVolumeL ?? null,
      infusionTempC: step.infusionTempC ?? null,
      thicknessLPerKg: step.thicknessLPerKg ?? null,
      decoction:
        step.decoction === undefined
          ? null
          : {
              thickness: step.decoction.thickness ?? 'thick',
              ratioLPerKg: step.decoction.ratioLPerKg ?? null,
              boilMinutes: step.decoction.boilMinutes ?? 20,
              restBeforeBoil: step.decoction.restBeforeBoil ?? null,
              pullVolumeL: step.decoction.pullVolumeL ?? null,
            },
    })),
  };
}

/** @see toMashProfile */
function toFermentationProfile(
  schedule: NamedSchedule<CoreFermentationStep>,
): FermentationProfileDoc {
  return {
    id: schedule.id,
    name: schedule.name,
    builtin: true,
    notes: schedule.description,
    createdAt: 0,
    updatedAt: 0,
    steps: schedule.steps.map((step, index) => ({
      stepId: `${schedule.id}-${index}`,
      name: step.name,
      type: step.type,
      tempC: step.tempC,
      endTempC: step.endTempC ?? null,
      days: step.days,
      pressurePsi: step.pressurePsi ?? null,
    })),
  };
}

/** The mash schedules this package ships, read-only. */
export const BUILTIN_MASH_PROFILES: readonly MashProfileDoc[] = MASH_SCHEDULES.map(toMashProfile);

/** The fermentation schedules this package ships, read-only. */
export const BUILTIN_FERMENTATION_PROFILES: readonly FermentationProfileDoc[] =
  FERMENTATION_SCHEDULES.map(toFermentationProfile);
